import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Upload, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  apiAdminshopsShopIdproductsIdPatch,
  apiMediasPost,
  getApiShopsShopIdproductsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import {
  ProductMetadataProductUpdateModelMetaKey,
  type ProductProductReadModel,
  type ProductProductUpdateJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import {
  buildCustomShopProductFormSchema,
  type CustomShopProductFormValues
} from './custom-shop-product-form-schema.ts';
import { CustomShopProductBrandSelect } from './custom-shop-product-brand-select.tsx';
import { getProductBrandIri } from './custom-shop-brands-api.ts';
import { CustomShopProductCategorySelect } from './custom-shop-product-category-select.tsx';
import { getProductCategoryIri } from './custom-shop-categories-api.ts';

interface UpdateCustomShopProductFormProps {
  shopId: number;
  product: ProductProductReadModel;
  onCancel: () => void;
  onSuccess: () => void;
}

/**
 * Met à jour un produit avec un JSON Merge Patch limité aux champs réellement
 * modifiés dans le formulaire.
 */
export function UpdateCustomShopProductForm({
  shopId,
  product,
  onCancel,
  onSuccess
}: UpdateCustomShopProductFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSchema = useMemo(() => buildCustomShopProductFormSchema(t), [t]);

  // Le code cadeau réel n'est jamais renvoyé par cette liste (seul publicMetadatas est exposé) :
  // le champ reste vide à l'édition, saisir une valeur remplace le code existant.
  const code = '';
  const codeDetail =
    product.publicMetadatas?.find((metadata) => metadata.metaKey === 'codeDetail')?.metaValue ?? '';
  const howtoUrl =
    product.publicMetadatas?.find((metadata) => metadata.metaKey === 'howtoUrl')?.metaValue ?? '';

  const form = useForm<CustomShopProductFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: product.name,
      description: product.description ?? '',
      price: product.price ?? undefined,
      tva: product.tva,
      shippingPrice: product.shippingPrice,
      categoryId: product.category?.id ?? null,
      brandId: product.brand?.id ?? null,
      code,
      codeDetail,
      howtoUrl,
      images: [],
      designationCom: product.designationCom ?? '',
      ean: product.ean ?? '',
      stock: product.stock ?? null,
      lowStockAlert: product.stock == null ? null : (product.lowStockAlert ?? null)
    },
    mode: 'onChange'
  });
  const stock = form.watch('stock');

  /** Téléverse une image produit et retourne son IRI pour le payload API. */
  const uploadImage = async (file: File): Promise<string> => {
    const media = await apiMediasPost({ file, type: 'layout' });
    if (!media.iri) throw new Error('Missing media IRI');
    return media.iri;
  };

  /** Envoie uniquement les champs modifiés via JSON Merge Patch. */
  const onSubmit = async (values: CustomShopProductFormValues) => {
    setIsSubmitting(true);

    try {
      const dirtyFields = form.formState.dirtyFields;
      // Sélectionner de nouvelles images remplace la collection existante après leur téléversement.
      const newImages = values.images?.length
        ? await Promise.all(values.images.map(uploadImage))
        : undefined;

      // Omettre les champs inchangés évite d'écraser des données mises à jour en parallèle.
      const payload: ProductProductUpdateJsonMergePatchModel = {
        ...(dirtyFields.name ? { name: values.name.trim() } : {}),
        ...(dirtyFields.description ? { description: values.description || null } : {}),
        ...(dirtyFields.price && !product.dynamicPrice ? { price: values.price ?? null } : {}),
        ...(dirtyFields.tva ? { tva: values.tva } : {}),
        ...(dirtyFields.shippingPrice ? { shippingPrice: values.shippingPrice } : {}),
        ...(values.categoryId !== (product.category?.id ?? null)
          ? {
              category: values.categoryId != null ? getProductCategoryIri(values.categoryId) : null
            }
          : {}),
        ...(values.brandId !== (product.brand?.id ?? null)
          ? {
              brand: values.brandId != null ? getProductBrandIri(values.brandId) : null
            }
          : {}),
        ...(dirtyFields.designationCom ? { designationCom: values.designationCom || null } : {}),
        ...(dirtyFields.ean ? { ean: values.ean || null } : {}),
        ...(dirtyFields.stock ? { stock: values.stock } : {}),
        ...(dirtyFields.lowStockAlert || (dirtyFields.stock && values.stock == null)
          ? { lowStockAlert: values.stock == null ? null : values.lowStockAlert }
          : {}),
        ...(dirtyFields.code || dirtyFields.codeDetail || dirtyFields.howtoUrl
          ? {
              metadatas: [
                ...(values.code
                  ? [
                      {
                        metaKey: ProductMetadataProductUpdateModelMetaKey.code,
                        metaValue: values.code
                      }
                    ]
                  : []),
                ...(values.codeDetail
                  ? [
                      {
                        metaKey: ProductMetadataProductUpdateModelMetaKey.codeDetail,
                        metaValue: values.codeDetail
                      }
                    ]
                  : []),
                ...(values.howtoUrl
                  ? [
                      {
                        metaKey: ProductMetadataProductUpdateModelMetaKey.howtoUrl,
                        metaValue: values.howtoUrl
                      }
                    ]
                  : [])
              ],
              // Le back refuse (422) une metadata "code" sans isDemat: true.
              ...(values.code ? { isDemat: true } : {})
            }
          : {}),
        ...(newImages ? { images: newImages } : {})
      };

      if (Object.keys(payload).length === 0) {
        onSuccess();
        return;
      }

      await apiAdminshopsShopIdproductsIdPatch(String(shopId), String(product.id), payload);
      await queryClient.invalidateQueries({
        queryKey: getApiShopsShopIdproductsGetCollectionQueryKey(String(shopId))
      });

      toast.success(t('admin.shops.products.update.success'));
      onSuccess();
    } catch (error) {
      showOrvalErrorToast(error, t('admin.shops.products.update.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.name.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={150} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="designationCom"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.designation.com.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={255} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.products.form.description.label')}</FormLabel>
              <FormControl>
                <Textarea rows={3} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="price"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.price.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    disabled={product.dynamicPrice}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(
                        event.target.value === '' ? undefined : event.target.valueAsNumber
                      )
                    }
                  />
                </FormControl>
                {product.dynamicPrice && (
                  <p className="text-xs text-muted-foreground">
                    {t('admin.shops.products.form.dynamic.price.readonly')}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ean"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('shop.product.ean.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={20} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="tva"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.tva.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    error={!!fieldState.error}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.tva.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shippingPrice"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.shipping.price.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    error={!!fieldState.error}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.shipping.price.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="categoryId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.category.label')}</FormLabel>
                <FormControl>
                  <CustomShopProductCategorySelect
                    shopId={shopId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="brandId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.brand.label')}</FormLabel>
                <FormControl>
                  <CustomShopProductBrandSelect
                    shopId={shopId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="stock"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.stock.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder={t('admin.shops.products.form.stock.placeholder')}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      field.onChange(value === '' ? null : event.target.valueAsNumber);
                      if (value === '') {
                        form.setValue('lowStockAlert', null, { shouldDirty: true });
                      }
                    }}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.stock.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lowStockAlert"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.low.stock.alert.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    disabled={stock == null}
                    placeholder={t('admin.shops.products.form.low.stock.alert.placeholder')}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(event.target.value === '' ? null : event.target.valueAsNumber)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.code.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      product.isDemat
                        ? t('admin.shops.products.form.code.placeholder.keep')
                        : undefined
                    }
                    {...field}
                  />
                </FormControl>
                {product.isDemat && (
                  <p className="text-xs text-muted-foreground">
                    {t('admin.shops.products.form.code.hint.keep')}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="codeDetail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.code.detail.label')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="howtoUrl"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.howto.url.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder={t('admin.shops.products.form.howto.url.placeholder')}
                    error={!!fieldState.error}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="images"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="update-custom-shop-product-images">
                {t('admin.shops.products.form.images.label')}
              </FormLabel>
              {product.images?.length ? (
                <div className="flex flex-wrap gap-2">
                  {product.images.map((image, index) =>
                    image.pathUri ? (
                      <img
                        key={`${image.pathUri}-${index}`}
                        src={image.pathUri}
                        alt=""
                        className="size-16 rounded-md border object-cover"
                      />
                    ) : null
                  )}
                </div>
              ) : null}
              <FormControl>
                <label className="flex min-h-24 cursor-pointer items-center justify-center gap-3 rounded-md border border-dashed bg-muted/20 p-4 text-center">
                  <Upload className="size-5 shrink-0" />
                  <span className="break-all text-sm">
                    {field.value?.length
                      ? t('admin.shops.products.form.images.replace.selected', {
                          count: field.value.length
                        })
                      : t('admin.shops.products.form.images.replace')}
                  </span>
                  <input
                    id="update-custom-shop-product-images"
                    className="sr-only"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => field.onChange(Array.from(event.target.files ?? []))}
                  />
                </label>
              </FormControl>
              <p className="text-xs text-muted-foreground">
                {t('admin.shops.products.form.images.replace.hint')}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            <X className="size-4" />
            {t('label.cancel')}
          </Button>
          <Button type="submit" rounded="full" className="flex-1" loading={isSubmitting}>
            {t('form.save.modifications')}
            <Check className="size-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
